import { css } from "@emotion/react";
import { usePermissions } from "@podtrac/common/accounts";
import { useVirtualizer } from "@tanstack/react-virtual";
import debounce from "lodash-es/debounce";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import { Link, useNavigate, useParams } from "react-router-dom";
import ShowIcon from "../shared/ShowIcon";
import { useAccountID } from "../store/account";
import { useShowID } from "../store/show";
import { pageSize, useShows, useShowsPages } from "../store/shows";
import { LayoutMainContext } from "../layout/Layout";
import DetailedChart from "../shared/DetailedChart";
import defaultIcon from "../../assets/default-icon.png";
import { ShowCard } from "./ShowCard";
import { FilterContext, SortContext, filterAtom, sortAtom } from "../dashboard/Dashboard";
import { useAtom } from "jotai";
import styles from "./ShowsCardView.module.scss";


const numberFormat = new Intl.NumberFormat().format;

const modalStyles = {
    content: {
        top: "10%",
        left: "10%",
        right: "10%",
        bottom: "auto"
        //marginRight: "-50%",
        //transform: "translate(-50%, -50%)"
    }
};

export function ShowsCardView() {
    const accountID = useAccountID();
    const showID = useShowID();

    const [modal, setModal] = React.useState();

    function openModal(name, range, value, prevValue, index, prevIndex, help, show) {
        setModal({ name, range, value, prevValue, index, prevIndex, help, show });
    }
    function closeModal() {
        setModal();
    }

    const [sort, setSort] = useAtom(sortAtom); // useContext(SortContext);
    const [filter, setFilter] = useAtom(filterAtom); // useContext(FilterContext);

    const [pages, setPages] = useState([0]);
    const queries = useShowsPages(accountID, sort, filter, pages);
    const itemCount = queries[0]?.data?.totalItems;
    const items = pages.reduce(
        (items, pageIndex, i) => {
            const data = queries[i].data;
            if (data) {
                items.itemCount = data.totalItems;
                items.pages[pageIndex] = data.items;
            }
            return items;
        },
        { pages: [] }
    );

    const onChange = useCallback(
        debounce(
            virtualizer => {
                //console.log(virtualizer.range, virtualizer);
                const { range } = virtualizer;
                if (!range) return;
                const { startIndex, endIndex } = range;
                const indexes = [];
                for (
                    let pageIndex = Math.floor(startIndex / pageSize);
                    pageIndex <= Math.floor(endIndex / pageSize);
                    pageIndex++
                ) {
                    indexes.push(pageIndex);
                }
                if (indexes.length) {
                    setPages(state => {
                        for (let pageIndex of indexes) {
                            if (!state.includes(pageIndex)) {
                                const pages = Array.from(new Set([...state, ...indexes]));
                                console.log("pages", pages);
                                return pages;
                            }
                        }

                        return state;
                    });
                }
            },
            200, // repeated calls before this many ms have elapsed are swallowed
            {
                leading: true, // but fire the first call immediately
                maxWait: 1000, // and let one through every maxWait ms even if the timer hasn't expired
                trailing: true // and make sure the last call is fired after the timer expires
            }
        ),
        [accountID, pageSize, filter]
    );

    // The scrollable element for your list
    const parentRef = useContext(LayoutMainContext); // React.useRef();

    const maxItems = 40000;
    // The virtualizer
    const rowVirtualizer = useVirtualizer({
        count: Math.min(itemCount, maxItems), // HACK to avoid exceeding max page size
        getScrollElement: () => parentRef.current,
        estimateSize: () => 500,
        overscan: 20,
        onChange
    });

    return (
        <>
            {/* The large inner element to hold all of the items */}
            <ul
                className={styles.virtualizer}
                style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    width: "100%",
                    position: "relative",
                    listStyle: "none"
                }}
            >
                {/* Only the visible items in the virtualizer, manually positioned to be in view */}
                {rowVirtualizer.getVirtualItems().map((virtualItem, index) => {
                    const pageIndex = Math.floor(virtualItem.index / pageSize);
                    const i = virtualItem.index % pageSize;
                    const show = items?.pages?.[pageIndex]?.[i];

                    return (
                        <ShowCard
                            key={virtualItem.key}
                            index={virtualItem.index}
                            ref={rowVirtualizer.measureElement}
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                //height: `${virtualItem.size}px`,
                                transform: `translateY(${ virtualItem.start /*- index * virtualItem.size*/ }px)`
                            }}
                            openModal={openModal}
                            showSummary={show}
                        />
                    );
                })}
            </ul>
            {itemCount > maxItems ? <p>Maximum items ({numberFormat(maxItems)}) exceeded. {numberFormat(itemCount - maxItems)} additional items not displayed.</p> : null }
            <MetricModal modal={modal} closeModal={closeModal} />
        </>
    );
}

function MetricModal({ modal, closeModal }) {
    const { help: Help, show: s = {} } = modal || {};
    const chartName = modal
        ? /Episode$/.test(modal.name)
            ? "Avg 7-day dl/ep"
            : `${modal.range} ${modal.name}`
        : "";
    return (
        <Modal
            isOpen={!!modal}
            onRequestClose={closeModal}
            style={modalStyles}
            contentLabel={s.showName}
        >
            {modal && (
                <>
                    <div
                        css={css`
                            display: flex;
                            align-items: center;

                            & > h1 {
                                margin: 0 0 0 0.5rem;
                                font-size: 1.5rem;
                                font-weight: 500;
                                color: #000;

                                & span {
                                    opacity: 0.65;
                                    display: inline-block;
                                    font-size: 0.8em;
                                    margin-left: 1em;
                                    text-transform: uppercase;
                                }

                                & strong {
                                    display: inline-block;
                                    margin-left: 0.5em;
                                }
                            }

                            margin-bottom: 1rem;
                        `}
                    >
                        <img
                            src={s.showImageUrl || defaultIcon}
                            css={css`
                                width: 32px;
                                border: 1px solid var(--border-color-low-contrast);
                            `}
                            onError={event => (event.target.src = defaultIcon)}
                        />
                        <h1
                            css={css`
                                flex: 1;
                            `}
                        >
                            {s.showName}{" "}
                            <span>
                                {modal.range} <strong>{modal.name}</strong>
                            </span>
                        </h1>
                        <div
                            onClick={closeModal}
                            css={css`
                                font-size: 1.5rem;
                                font-weight: 500;
                                color: #999;
                                cursor: pointer;
                                &:hover {
                                    color: #000;
                                }
                            `}
                        >
                            ×
                        </div>
                    </div>

                    {modal.index ? (
                        <DetailedChart
                            data={s.sparklines}
                            index={modal.index}
                            prevIndex={modal.prevIndex}
                            dateIndex={"measurementDate"}
                            name={chartName}
                        />
                    ) : null}
                    <div
                        css={css`
                            width: 75vw;
                            max-height: 60vh;
                            overflow: auto;
                            font-size: 0.9rem;
                            padding-right: 0.5rem;
                            & > h2 {
                                text-transform: none;
                                margin: 0.5rem 0 1rem;
                                color: initial;
                                font-weight: 500;
                            }
                        `}
                    >
                        <Help />
                    </div>
                </>
            )}
        </Modal>
    );
}
